
import { useCandidateData } from "./useCandidateData";
import { useCandidateComments } from "./useCandidateComments";
import { useCandidateActions } from "./useCandidateActions";
import { Candidate, CandidateComment } from "@/types/candidate";
import { useState, useCallback } from "react";

export const useCandidateDetail = (id: string | undefined): {
  loading: boolean;
  candidate: Candidate | null;
  cv: string | null;
  cvExists: boolean;
  comments: CandidateComment[];
  handleDelete: () => Promise<void>;
  handleAddComment: (content: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;
  handleUpdateCandidate: (updatedData: Partial<Candidate>) => Promise<void>;
  refetchCandidate: () => Promise<void>;
} => {
  const { loading, candidate, cv, cvExists, refetch } = useCandidateData(id);
  const { comments, handleAddComment, handleUpdateComment, handleDeleteComment } = useCandidateComments(id);
  const { handleDelete, handleUpdateCandidate } = useCandidateActions(id);
  
  // Local state for updating candidate data
  const [localCandidate, setLocalCandidate] = useState<Candidate | null>(candidate);
  
  // Update local candidate when candidate from API changes
  if (candidate !== null && JSON.stringify(candidate) !== JSON.stringify(localCandidate)) {
    setLocalCandidate(candidate);
  }
  
  // Enhanced handleUpdateCandidate that also updates local state
  const handleUpdate = async (updatedData: Partial<Candidate>) => {
    console.log("Updating candidate with data:", updatedData);
    await handleUpdateCandidate(updatedData);
    
    // Local state update will happen after refetch
  };

  // Create a refetch function that updates the local candidate data
  const refetchCandidate = useCallback(async () => {
    console.log("Refetching candidate data...");
    if (refetch) {
      try {
        const freshData = await refetch();
        console.log("Refetched candidate data:", freshData);
        if (freshData) {
          setLocalCandidate(freshData);
        }
      } catch (error) {
        console.error("Error refetching candidate data:", error);
      }
    }
  }, [refetch]);

  return {
    loading,
    candidate: localCandidate,
    cv,
    cvExists,
    comments,
    handleDelete,
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    handleUpdateCandidate: handleUpdate,
    refetchCandidate
  };
};
